import { useState, useCallback, useEffect } from 'react';
import { useEditor } from '@tiptap/react';
import { mergeAttributes, Node, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { TiptapEditor } from '@/components/ui/TiptapEditor';
import { ImageUploadModal } from '@/components/ui/ImageUploadModal';
import { LinkModal } from '@/components/ui/LinkModal';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  LinkIcon,
  ImageIcon,
  ListIcon,
  ChevronDownIcon,
} from '@/components/icons';
import {
  Strikethrough,
  Code,
  Code2,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  // Table2,
  SeparatorHorizontal,
  Undo2,
  Redo2,
} from 'lucide-react';
import { cn } from '@/utils/helpers/classnames';

const Figcaption = Node.create({
  name: 'figcaption',
  content: 'inline*',
  parseHTML() {
    return [{ tag: 'figcaption' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['figcaption', mergeAttributes(HTMLAttributes), 0];
  },
});

const Figure = Node.create({
  name: 'figure',
  group: 'block',
  content: 'image figcaption?',
  draggable: true,
  parseHTML() {
    return [{ tag: 'figure' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['figure', mergeAttributes(HTMLAttributes), 0];
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize || null,
            renderHTML: attributes => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        fontSize =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).run(),
    };
  },
});

const FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px', '24px', '30px', '36px'] as const;

// ─── Toolbar configuration ────────────────────────────────────────────────────

export type ToolbarOptions = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  link?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
};

const DEFAULT_TOOLBAR: Required<ToolbarOptions> = {
  bold: true,
  italic: true,
  underline: true,
  link: true,
  bulletList: true,
  orderedList: true,
};

// ─── Extended toolbar ─────────────────────────────────────────────────────────

/**
 * All TipTap features available from the configured extensions that are NOT
 * already in the primary toolbar. Visibility is controlled by
 * `showExtendedToolbar` (default: true). Individual items default to `true`
 * and can be hidden with `false` via the `extendedToolbar` prop.
 */
export type ExtendedToolbarOptions = {
  /** Image upload/insert. Default: true */
  image?: boolean;
  /** Heading / paragraph style dropdown. Default: true */
  textStyle?: boolean;
  /** Font-size dropdown. Default: true */
  fontSize?: boolean;
  /** Strikethrough text. Default: true */
  strikethrough?: boolean;
  /** Inline code mark. Default: true */
  inlineCode?: boolean;
  /** Fenced code block. Default: true */
  codeBlock?: boolean;
  /** Block quotation. Default: true */
  blockquote?: boolean;
  /** Horizontal rule / thematic break. Default: true */
  horizontalRule?: boolean;
  /** Align current block to left. Default: true */
  alignLeft?: boolean;
  /** Align current block to center. Default: true */
  alignCenter?: boolean;
  /** Align current block to right. Default: true */
  alignRight?: boolean;
  /** Justify current block. Default: true */
  alignJustify?: boolean;
  /** Insert a basic table. Default: true */
  table?: boolean;
  /** Undo last action. Default: true */
  undo?: boolean;
  /** Redo last undone action. Default: true */
  redo?: boolean;
};

const DEFAULT_EXTENDED_TOOLBAR: Required<ExtendedToolbarOptions> = {
  image: true,
  textStyle: true,
  fontSize: true,
  strikethrough: true,
  inlineCode: true,
  codeBlock: true,
  blockquote: true,
  horizontalRule: true,
  alignLeft: true,
  alignCenter: true,
  alignRight: true,
  alignJustify: true,
  table: true,
  undo: true,
  redo: true,
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RichTextEditorProps {
  /**
   * Controlled HTML value. When omitted the editor is uncontrolled.
   * Works naturally as the `field.value` of a React-Hook-Form <Controller>.
   */
  value?: string;
  /** Called on every content change with the current HTML string. */
  onChange?: (html: string) => void;
  /** Called when the editor loses focus. Useful for form libraries like react-hook-form. */
  onBlur?: () => void;
  /** Tiptap placeholder text shown when the editor is empty. */
  placeholder?: string;
  /** Disables all editing and toolbar buttons. */
  disabled?: boolean;
  /**
   * Override individual toolbar items.
   * Unspecified items default to `true` (visible).
   *
   * @example
   * // Hide image and link buttons
   * <RichTextEditor toolbar={{ image: false, link: false }} />
   */
  toolbar?: ToolbarOptions;
  /** Extra class names applied to the outermost wrapper div. */
  className?: string;
  /** Extra class names applied to the toolbar container div. */
  toolbarContainerClassName?: string;
  /** Extra class names applied to the content area div. */
  contentAreaClassName?: string;
  /**
   * Controls visibility of the extended toolbar group.
   * Defaults to `true`.
   */
  showExtendedToolbar?: boolean;
  /**
   * Override individual buttons in the extended toolbar group.
   * Unspecified items default to `true`.
   */
  extendedToolbar?: ExtendedToolbarOptions;
  label?: string | React.ReactNode;
  error?: string;
  required?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Divider = () => <div className="h-6 border-l border-gray-300 mx-1" />;

/** Only renders a divider when at least one item in each adjacent group is visible. */
function useSeparator(toolbar: Required<ToolbarOptions>) {
  const sectionB = toolbar.bold || toolbar.italic || toolbar.underline;
  const sectionC = toolbar.link;
  const sectionD = toolbar.bulletList || toolbar.orderedList;

  return {
    afterB: sectionB && (sectionC || sectionD),
    afterC: sectionC && sectionD,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RichTextEditor({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Start writing...',
  disabled = false,
  toolbar: toolbarProp,
  className = '',
  toolbarContainerClassName = '',
  contentAreaClassName = '',
  showExtendedToolbar = true,
  extendedToolbar: extendedToolbarProp,
  label,
  error,
  required,
}: RichTextEditorProps) {
  const toolbar = { ...DEFAULT_TOOLBAR, ...toolbarProp } as Required<ToolbarOptions>;
  const sep = useSeparator(toolbar);
  const hasExtendedGroup = showExtendedToolbar;
  const ext = { ...DEFAULT_EXTENDED_TOOLBAR, ...(extendedToolbarProp ?? {}) } as Required<ExtendedToolbarOptions>;

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageCaption, setImageCaption] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalUrl, setLinkModalUrl] = useState('');

  const normalizeEditorHtml = useCallback((nextEditor: { isEmpty: boolean; getHTML: () => string }) => {
    return nextEditor.isEmpty ? '' : nextEditor.getHTML();
  }, []);

  const editor = useEditor(({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextStyle,
      FontSize,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-forestGreen-100 underline' },
      }),
      Image,
      Figure,
      Figcaption,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      handleDOMEvents: {
        blur: () => {
          onBlur?.();
          return false;
        },
      },
    },
    onUpdate: ({ editor }: { editor: { isEmpty: boolean; getHTML: () => string } }) => {
      onChange?.(normalizeEditorHtml(editor));
    },
  }) as unknown as Parameters<typeof useEditor>[0]);

  type ExtendedChain = {
    setFontSize: (fontSize: string) => { run: () => boolean };
    unsetFontSize: () => { run: () => boolean };
    setTextAlign: (alignment: 'left' | 'center' | 'right' | 'justify') => { run: () => boolean };
    deleteTable: () => { run: () => boolean };
    insertTable: (options: { rows: number; cols: number; withHeaderRow: boolean }) => { run: () => boolean };
  };

  const getExtendedChain = () => {
    if (!editor) return null;
    return editor.chain().focus() as unknown as ExtendedChain;
  };

  // Keep the editor in sync when the external value changes (e.g. form reset).
  useEffect(() => {
    if (!editor || normalizeEditorHtml(editor) === (value || '')) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor, normalizeEditorHtml]);

  // Keep editability in sync with the disabled prop.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  // ── Link handler ──────────────────────────────────────────────────────────

  const openLinkModal = useCallback(() => {
    if (!editor) return;
    setLinkModalUrl(editor.getAttributes('link').href || '');
    setShowLinkModal(true);
  }, [editor]);

  const handleInsertLink = (url: string, text?: string) => {
    if (!editor) return;
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const { from, to } = editor.state.selection;
    if (from !== to) {
      editor.chain().focus().setLink({ href: url }).run();
    } else if (text?.trim()) {
      editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
    } else {
      editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
    }
  };

  // ── Image handler ─────────────────────────────────────────────────────────

  const openImageModal = useCallback(() => {
    if (!editor) return;
    setImageCaption('');
    setShowImageModal(true);
  }, [editor]);

  const handleInsertImage = (imageUrl: string) => {
    if (!editor) return;

    const caption = imageCaption.trim();
    if (caption) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'figure',
          content: [
            { type: 'image', attrs: { src: imageUrl } },
            {
              type: 'figcaption',
              content: [{ type: 'text', text: caption }],
            },
          ],
        })
        .run();
      return;
    }

    editor.chain().focus().setImage({ src: imageUrl }).run();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={cn(`relative rounded-lg border border-gray-300`, className)}>
      {/* Label */}
     {label &&  <div className="mb-1">
     { typeof label === 'string' ? (
        <label className={cn('text-sm font-medium', required ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : '')}>
          {label}
        </label>
      ) : (
        label
      )}
      </div>}
 
  <div className="border rounded-lg border-gray-300 overflow-visible">
      {editor && (
   <div className={cn(`relative flex items-center gap-1 border-b border-gray-300 flex-wrap bg-gray-50 px-2 py-1 overflow-visible rounded-t-lg`, toolbarContainerClassName)}>

          {/* ── Bold ─────────────────────────────────────────────────────── */}
          {toolbar.bold && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('bold') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Bold"
            >
              <BoldIcon className="w-5 h-5" />
            </button>
          )}

          {/* ── Italic ───────────────────────────────────────────────────── */}
          {toolbar.italic && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('italic') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Italic"
            >
              <ItalicIcon className="w-5 h-5" />
            </button>
          )}

          {/* ── Underline ────────────────────────────────────────────────── */}
          {toolbar.underline && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('underline') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Underline"
            >
              <UnderlineIcon className="w-5 h-5" />
            </button>
          )}

          {sep.afterB && <Divider />}

          {/* ── Link ─────────────────────────────────────────────────────── */}
          {toolbar.link && (
            <button
              type="button"
              disabled={disabled}
              onClick={openLinkModal}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('link') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Add Link"
            >
              <LinkIcon className="w-5 h-5" />
            </button>
          )}

          {sep.afterC && <Divider />}

          {/* ── Bullet list ──────────────────────────────────────────────── */}
          {toolbar.bulletList && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('bulletList') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Bullet List"
            >
              <ListIcon className="w-5 h-5" />
            </button>
          )}

          {/* ── Ordered list ─────────────────────────────────────────────── */}
          {toolbar.orderedList && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('orderedList') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Numbered List"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h12M9 12h12M9 19h12M3 5.5v3M3 12h1M3 19h1M3 8.5h1" />
              </svg>
            </button>
          )}

          {/* ── Extended group ─────────────────────────────────────────── */}
          {hasExtendedGroup && <Divider />}

          {/* ── Image (extended) ───────────────────────────────────────── */}
          {hasExtendedGroup && ext.image && (
            <button
              type="button"
              disabled={disabled}
              onClick={openImageModal}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add Image"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
          )}

          {hasExtendedGroup && ext.image && (ext.textStyle || ext.fontSize || ext.strikethrough || ext.inlineCode || ext.codeBlock || ext.blockquote || ext.horizontalRule || ext.alignLeft || ext.alignCenter || ext.alignRight || ext.alignJustify || ext.table || ext.undo || ext.redo) && (
            <Divider />
          )}

          {/* ── Text style dropdown (extended) ─────────────────────────── */}
          {hasExtendedGroup && ext.textStyle && (
            <div className="relative group">
              <button
                type="button"
                disabled={disabled}
                className="flex items-center gap-2 text-sm font-semibold p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editor.isActive('heading', { level: 1 })
                  ? 'Heading 1'
                  : editor.isActive('heading', { level: 2 })
                  ? 'Heading 2'
                  : editor.isActive('heading', { level: 3 })
                  ? 'Heading 3'
                  : 'Text Style'}
                <ChevronDownIcon className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                <button type="button" disabled={disabled} onClick={() => editor.chain().focus().setParagraph().run()} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Paragraph</button>
                <button type="button" disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Heading 1</button>
                <button type="button" disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Heading 2</button>
                <button type="button" disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50">Heading 3</button>
              </div>
            </div>
          )}

          {/* ── Font-size dropdown (extended) ──────────────────────────── */}
          {hasExtendedGroup && ext.fontSize && (
            <div className="relative group">
              <button
                type="button"
                disabled={disabled}
                className="flex items-center gap-2 text-sm font-semibold p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {((editor.getAttributes('textStyle').fontSize as string | undefined) || '14px').replace('px', '')}
                <ChevronDownIcon className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[90px]">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => getExtendedChain()?.unsetFontSize().run()}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
                >
                  Default
                </button>
                {FONT_SIZE_OPTIONS.map(size => (
                  <button
                    key={size}
                    type="button"
                    disabled={disabled}
                    onClick={() => getExtendedChain()?.setFontSize(size).run()}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
                  >
                    {size.replace('px', '')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasExtendedGroup && (ext.textStyle || ext.fontSize) && (ext.strikethrough || ext.inlineCode || ext.codeBlock || ext.blockquote || ext.horizontalRule || ext.alignLeft || ext.alignCenter || ext.alignRight || ext.alignJustify || ext.table || ext.undo || ext.redo) && (
            <Divider />
          )}

          {/* ── Strikethrough ──────────────────────────────────────────── */}
          {hasExtendedGroup && ext.strikethrough && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('strike') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Strikethrough"
            >
              <Strikethrough className="w-4 h-4" />
            </button>
          )}

          {/* ── Inline code ──────────────────────────────────────────── */}
          {hasExtendedGroup && ext.inlineCode && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('code') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Inline Code"
            >
              <Code className="w-4 h-4" />
            </button>
          )}

          {/* ── Code block ───────────────────────────────────────────── */}
          {hasExtendedGroup && ext.codeBlock && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('codeBlock') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Code Block"
            >
              <Code2 className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && (ext.strikethrough || ext.inlineCode || ext.codeBlock) && (ext.blockquote || ext.horizontalRule || ext.alignLeft || ext.alignCenter || ext.alignRight || ext.alignJustify || ext.table || ext.undo || ext.redo) && (
            <Divider />
          )}

          {/* ── Blockquote ───────────────────────────────────────────── */}
          {hasExtendedGroup && ext.blockquote && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('blockquote') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Blockquote"
            >
              <Quote className="w-4 h-4" />
            </button>
          )}

          {/* ── Horizontal rule ────────────────────────────────────────── */}
          {hasExtendedGroup && ext.horizontalRule && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Horizontal Rule"
            >
              <SeparatorHorizontal className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && (ext.blockquote || ext.horizontalRule) && (ext.alignLeft || ext.alignCenter || ext.alignRight || ext.alignJustify || ext.table || ext.undo || ext.redo) && (
            <Divider />
          )}

          {/* ── Alignment ─────────────────────────────────────────────── */}
          {hasExtendedGroup && ext.alignLeft && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => getExtendedChain()?.setTextAlign('left').run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive({ textAlign: 'left' }) ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && ext.alignCenter && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => getExtendedChain()?.setTextAlign('center').run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive({ textAlign: 'center' }) ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && ext.alignRight && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => getExtendedChain()?.setTextAlign('right').run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive({ textAlign: 'right' }) ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && ext.alignJustify && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => getExtendedChain()?.setTextAlign('justify').run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title="Justify"
            >
              <AlignJustify className="w-4 h-4" />
            </button>
          )}

          {hasExtendedGroup && (ext.alignLeft || ext.alignCenter || ext.alignRight || ext.alignJustify) && (ext.table || ext.undo || ext.redo) && (
            <Divider />
          )}

          {/* ── Table ─────────────────────────────────────────────────── */}
          {/* {hasExtendedGroup && ext.table && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                const chain = getExtendedChain();
                if (!chain) return;
                if (editor.isActive('table')) {
                  chain.deleteTable().run();
                  return;
                }
                chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
              }}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                editor.isActive('table') ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
              }`}
              title={editor.isActive('table') ? 'Remove Table' : 'Insert Table'}
            >
              <Table2 className="w-4 h-4" />
            </button>
          )} */}

          {/* {hasExtendedGroup && ext.table && (ext.undo || ext.redo) && (
            <Divider />
          )} */}

          {/* ── Undo ─────────────────────────────────────────────────── */}
          {hasExtendedGroup && ext.undo && (
            <button
              type="button"
              disabled={disabled || !editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo"
            >
              <Undo2 className="w-4 h-4" />
            </button>
          )}

          {/* ── Redo ─────────────────────────────────────────────────── */}
          {hasExtendedGroup && ext.redo && (
            <button
              type="button"
              disabled={disabled || !editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo"
            >
              <Redo2 className="w-4 h-4" />
            </button>
          )}

        </div>
      )}

      <div className={cn(`relative z-0 bg-white p-4 min-h-60 max-h-96 overflow-y-auto overflow-x-hidden rounded-b-lg`, contentAreaClassName)} style={{ scrollbarWidth: 'thin', scrollbarColor: '#D1D5DB #F3F4F6' }}>
        <TiptapEditor editor={editor} />
      </div>
</div>
      {
        error && <p className="absolute -bottom-4 text-xs text-red-600">{error}</p>
      }

      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onInsert={handleInsertImage}
        caption={imageCaption}
        onCaptionChange={setImageCaption}
      />

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => { setShowLinkModal(false); setLinkModalUrl(''); }}
        onInsert={handleInsertLink}
        initialUrl={linkModalUrl}
      />
    </div>
  );
}

export default RichTextEditor;
